import { createSlice } from '@reduxjs/toolkit';
import type { ActionCreatorWithoutPayload, ActionReducerMapBuilder } from '@reduxjs/toolkit';

import type { JsonFailure, RequestStatus } from '@skynet/data-ws-core';
import { isOkCommand, parseResponse, registerErrorMessage } from '@skynet/data-ws-core';
import { actionOnMessage, createMmWsAction } from '@skynet/data-ws-mm';

import type {
  FormKpi,
  JsonKpi,
  JsonKpiSuccess,
  KpiCommandMap,
  KpiState,
  PayloadKpi,
} from './types';
import { KpiCommands } from './types';

function initKpiStateFactory(opts?: Partial<KpiState>): KpiState {
  const initialState: KpiState = {
    error: null,
    requestStatus: null,
    formKpi: null,
    kpiList: null,
    selectedEditKpi: null,
    resAddKpi: null,
    resEditKpi: null,
    resRemoveKpi: null,
  };
  return { ...initialState, ...opts };
}

registerErrorMessage(KpiCommands.AddKPI, {
  title: 'Failed to add KPI',
  getDescription: (json: JsonFailure) => String(json.message),
});
registerErrorMessage(KpiCommands.EditKPI, {
  title: 'Failed to update KPI',
  getDescription: (json: JsonFailure) => String(json.message),
});
registerErrorMessage(KpiCommands.RemoveKPI, {
  title: 'Failed to remove KPI',
  getDescription: (json: JsonFailure) => String(json.message),
});

/**
 * Props for the shared KPI slice factory.
 *
 * @typeParam Commands - The per-domain command map, combining the shared
 * {@link KpiCommands} with the domain listen command (`KPI`).
 */
export interface InitKpiSliceProps<Commands extends KpiCommandMap> {
  /** Action prefix used for the emitted WS action types (e.g. `loans`). */
  prefix?: string;
  /** Redux slice name (e.g. `loansKpi`). */
  sliceName?: string;
  /** Optional pre-seeded state. */
  initialState?: KpiState;
  /** Optional hook to register additional reducers on the slice builder. */
  extraReducers?: (builder: ActionReducerMapBuilder<KpiState>) => void;
  /** Combined command map for the domain. Required. */
  commands?: Commands;
  /**
   * Global reset action; when dispatched the slice returns to its initial
   * state. The app owns this action and injects it here (the package must not
   * depend on app-level store wiring).
   */
  resetAction: ActionCreatorWithoutPayload;
}

/**
 * Creates a KPI Redux slice (reducer + actions) for a single domain. The slice
 * handles the shared add/edit/remove WS commands plus the domain-specific
 * `KPI` listen command, and resets on the injected {@link InitKpiSliceProps.resetAction}.
 */
export function initKpiSlice<Commands extends KpiCommandMap>({
  prefix = 'shared',
  sliceName = 'kpi',
  initialState = initKpiStateFactory(),
  extraReducers,
  commands,
  resetAction,
}: InitKpiSliceProps<Commands>) {
  if (!commands) {
    throw new Error('Commands are required for KPI slice factory');
  }

  const allowedCommands = Object.values(commands).map((cmd) => String(cmd));

  const addKpi = createMmWsAction<PayloadKpi, PayloadKpi>(
    `${prefix}/addKpi`,
    (data: PayloadKpi) => ({
      command: commands.AddKPI,
      ...data,
    }),
  );

  const editKpi = createMmWsAction<PayloadKpi, PayloadKpi>(
    `${prefix}/editKpi`,
    (data: PayloadKpi) => ({
      command: commands.EditKPI,
      ...data,
    }),
  );

  const removeKpi = createMmWsAction<number, { id: number }>(`${prefix}/removeKpi`, (id) => ({
    command: commands.RemoveKPI,
    id,
  }));

  const kpiSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      setSelectedEditKpi(state, action: { payload: JsonKpi | null }) {
        state.selectedEditKpi = action.payload;
        state.resEditKpi = null;
      },

      resetStatus(state) {
        state.resAddKpi = null;
      },

      setFormKpi(state, action: { payload: FormKpi[] }) {
        state.formKpi = action.payload;
      },

      setKpiRequestStatus(state, action: { payload: RequestStatus }) {
        state.requestStatus = action.payload;
      },
    },
    extraReducers: (builder) => {
      // listen
      if (typeof extraReducers === 'function') {
        extraReducers(builder);
      }

      builder.addCase(addKpi, (state) => {
        state.resAddKpi = 'pending';
      });

      builder.addCase(editKpi, (state) => {
        state.resEditKpi = 'pending';
      });

      builder.addCase(removeKpi, (state) => {
        state.resRemoveKpi = 'pending';
      });

      // on WS message
      builder.addCase(actionOnMessage, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}/processing ${actionOnMessage.toString()}`,
          action,
          allowedCommands,
        );

        if (error) {
          state.error = error;

          switch (errorCommand) {
            case commands.AddKPI:
              state.resAddKpi = 'error';
              break;
            case commands.EditKPI:
              state.resEditKpi = 'error';
              break;
            case commands.RemoveKPI:
              state.resRemoveKpi = 'error';
              break;
            case undefined:
            default:
          }
        }

        const { command, okCommand } = json;

        if (isOkCommand(command) && okCommand === commands.AddKPI) {
          state.formKpi = null;
          state.resAddKpi = 'success';
        }
        if (isOkCommand(command) && okCommand === commands.EditKPI) {
          state.resEditKpi = 'success';
          state.selectedEditKpi = null;
        }

        if (isOkCommand(command) && okCommand === commands.RemoveKPI) {
          state.resRemoveKpi = 'success';
          return;
        }

        if (command === commands.KPI) {
          const { kpi } = json as JsonKpiSuccess;
          state.kpiList = kpi;
        }
      });

      builder.addCase(resetAction, () => {
        return initialState;
      });
    },
  });

  const actions = {
    ...kpiSlice.actions,
    addKpi,
    editKpi,
    removeKpi,
  };

  return {
    actions,
    reducer: kpiSlice.reducer,
  };
}
